import React, { useState } from "react";
import { Navigate, Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { t } from "../i18n/translations";
import { CalendarDays, Sparkles, Clock, Users, LogOut, LayoutDashboard, Settings, Gift, Mail, Hourglass, Menu, X, ImagePlus, TrendingUp } from "lucide-react";
import InstallPrompt from "./InstallPrompt";

const LOGO = "https://customer-assets.emergentagent.com/job_massage-portal-6/artifacts/g3nld1od_logo_transparent_background.webp";

export const AdminLayout = () => {
  const { user, loading, logout } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  React.useEffect(() => { setOpen(false); }, [location.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-muted">…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const navs = [
    { to: "/admin", icon: LayoutDashboard, key: "dashboard", end: true },
    { to: "/admin/bookings", icon: CalendarDays, key: "bookings" },
    { to: "/admin/waitlist", icon: Hourglass, key: "waitlist" },
    { to: "/admin/services", icon: Sparkles, key: "services" },
    { to: "/admin/availability", icon: Clock, key: "availability" },
    { to: "/admin/customers", icon: Users, key: "customers" },
    { to: "/admin/finances", icon: TrendingUp, key: "finances" },
    { to: "/admin/vouchers", icon: Gift, key: "vouchers" },
    { to: "/admin/messages", icon: Mail, key: "messages" },
    { to: "/admin/hero-images", icon: ImagePlus, key: "hero_images" },
    { to: "/admin/settings", icon: Settings, key: "settings" },
  ];

  const SidebarContent = ({ onClick }) => (
    <>
      <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
        <img src={LOGO} alt="Ayurveda Aargau" className="h-14 w-auto object-contain" />
        {onClick && (
          <button onClick={onClick} className="p-2 lg:hidden" aria-label="Schliessen">
            <X className="w-6 h-6 text-sand" strokeWidth={1.5} />
          </button>
        )}
      </div>
      <div className="px-6 py-2 text-xs uppercase tracking-widest text-sand/50">Admin</div>
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navs.map((n) => (
          <NavLink
            key={n.key}
            to={n.to}
            end={n.end}
            data-testid={`admin-nav-${n.key}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-3 rounded-md text-sm transition-colors ${isActive ? "bg-terracotta text-white" : "text-sand/80 hover:bg-white/5"}`
            }
          >
            <n.icon className="w-4 h-4" strokeWidth={1.5} />
            {t(lang, `admin.${n.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2 text-xs text-sand/50 truncate">{user.email}</div>
        <button
          onClick={handleLogout}
          data-testid="admin-logout-btn"
          className="w-full flex items-center gap-3 px-3 py-3 rounded-md text-sm text-sand/80 hover:bg-white/5"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          {t(lang, "admin.logout")}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-sand-soft" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 bg-ink text-sand flex-col shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 h-16 bg-ink text-sand flex items-center justify-between px-4 border-b border-white/10">
        <button onClick={() => setOpen(true)} className="p-2 -ml-2" data-testid="admin-mobile-menu-btn" aria-label="Menu öffnen">
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
        <img src={LOGO} alt="Ayurveda Aargau" className="h-10 w-auto object-contain" />
        <button onClick={handleLogout} className="p-2 -mr-2" data-testid="admin-mobile-logout" aria-label="Abmelden">
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 bg-ink/60 z-50 animate-fade-in" onClick={() => setOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 bottom-0 z-50 w-72 bg-ink text-sand flex flex-col shadow-2xl animate-fade-up" data-testid="admin-mobile-drawer">
            <SidebarContent onClick={() => setOpen(false)} />
          </aside>
        </>
      )}

      <main className="flex-1 overflow-auto pt-16 lg:pt-0">
        <Outlet />
      </main>
      <InstallPrompt />
    </div>
  );
};
